import * as Sentry from '@sentry/browser';
import { forOwn, isString } from 'lodash-es';

import { isObject } from '@iconik-io/core/src/is_object';
import type { EndpointRequest } from '@iconik-io/iconik-api/src/client/request';
import {
  type AnyEndpointResponse,
  EndpointError,
} from '@iconik-io/iconik-api/src/client/response';

type UnknownRecord = Record<string, unknown>;

// convert error to string and remove {} [] and replace " with space
const stringify = (error: UnknownRecord): string =>
  JSON.stringify(error)
    .replace(/[{()}]/g, '')
    .replace(/[[\]']+/g, '')
    .replace(/"/g, ' ');

function parseErrorsObject(data: UnknownRecord | UnknownRecord[]): string[] {
  if (Array.isArray(data)) {
    return data.map((error) => (isObject(error) ? stringify(error) : error));
  }
  if (isObject(data)) {
    const errors: string[] = [];
    forOwn(data, (item, key) => {
      errors.push(`${key}: ${isObject(item) ? stringify(item) : item}`);
    });
    return errors;
  }
  return [];
}

// The response is optional: a rejection that never reached the server (an
// aborted request, a failure thrown while handling the response) carries no
// response at all.
export const parseErrorFromEndpointResponse = (
  response: AnyEndpointResponse | undefined,
): string[] => {
  let data;

  if (response?.body?.errors) {
    data = response.body.errors;
  } else if (response?.statusText) {
    data = response.statusText;
  }

  let messages: string[] = [];
  if (Array.isArray(data)) {
    messages.push(data.join());
  } else if (isString(data)) {
    messages = [data];
  } else if (data) {
    messages = [...messages, ...parseErrorsObject(data)];
  }
  return messages;
};

export const isInvalidPasswordError = (error: unknown): boolean =>
  error instanceof EndpointError &&
  error.response?.body?.errors?.[0] === 'Invalid password';

export const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof EndpointError && error.response?.status === 401;

export const isForbiddenError = (error: unknown): boolean =>
  error instanceof EndpointError && error.response?.status === 403;

export const isBadRequestError = (error: unknown): boolean =>
  error instanceof EndpointError && error.response?.status === 400;

export const handleErrors = <Request extends EndpointRequest>(
  error: EndpointError<Request>,
): string[] => {
  const response = error.response;
  let messages = parseErrorFromEndpointResponse(response);
  if (!messages.length) {
    Sentry.captureException(error, {
      level: 'error',
      extra: {
        response,
      },
    });
    messages = [];
  }
  return messages;
};
